import { GetSnowflakeJobItem, SnowflakeJobFilters } from "@/pages/query/types";
import { FilterType, TableHeaderType } from "@/components/tables/types";
import { formatBytes, formatCost, formatMilliseconds } from "@/helpers/utils";
import { cn } from "@/lib/utils";
import { Link, createSearchParams } from "react-router-dom";

interface UseDbtJobsTableHeadersProps {
  currency: string;
  currencySymbol: string;
  filters?: SnowflakeJobFilters;
  defaultTab?: string;
}

const getProjectUrl = (item: GetSnowflakeJobItem, defaultTab = "models") => ({
  pathname: `/query/dbt-jobs/${encodeURIComponent(item.workflow_id)}/${defaultTab}`,
  search: createSearchParams({
    project: item.dbt_project_name ?? "",
    integration: item.integration_environment ?? "-",
  }).toString(),
});

export const useDbtJobsTableHeaders = ({
  currency,
  currencySymbol,
  filters,
  defaultTab,
}: UseDbtJobsTableHeadersProps): TableHeaderType<GetSnowflakeJobItem>[] => {
  return [
    {
      id: 1,
      label: "Project Name",
      uid: "dbt_project_name",
      render: (item: GetSnowflakeJobItem) => (
        <Link to={getProjectUrl(item, defaultTab)} className="al-text-brand al-break-all">
          {item.dbt_project_name ?? "-"}
        </Link>
      ),
    },
    {
      id: 2,
      label: "Total Cost",
      uid: "query_cost",
      render: (item: GetSnowflakeJobItem) => <div>{formatCost(item.query_cost, currency)}</div>,
      sort: {
        sortSearchKey: "sortAttribute",
      },
      filter: {
        filterType: FilterType.TEXT,
        searchKey: "query_cost",
        filterMode: "gt",
        filterModeSearchKey: "costFilterMode",
        placeHolder: `Specify cost in ${currencySymbol}`,
        label: "Total Cost",
      },
    },
    {
      id: 3,
      label: "# Runs",
      uid: "runs",
      render: (item: GetSnowflakeJobItem) => <div>{item.runs}</div>,
      sort: {
        sortSearchKey: "sortAttribute",
      },
    },
    {
      id: 4,
      label: "Test Coverage",
      uid: "test_coverage",
      render: (item: GetSnowflakeJobItem) => (
        <div>{item.test_coverage ? `${item.test_coverage}%` : "-"}</div>
      ),
    },
    {
      id: 5,
      label: "Average Execution Time",
      uid: "average_execution_time",
      render: (item: GetSnowflakeJobItem) => <div>{formatMilliseconds(item.execution_time)}</div>,
      sort: {
        sortSearchKey: "sortAttribute",

        sortParamValue: "execution_time",
      },
      filter: {
        filterType: FilterType.TEXT,
        searchKey: "executionTime",
        filterMode: "gt",
        filterModeSearchKey: "executionTimeFilterMode",
        placeHolder: "Specify time in seconds",
        label: "Execution Time",
      },
    },
    {
      id: 6,
      label: "Bytes Scanned",
      uid: "bytes_scanned",
      render: (item: GetSnowflakeJobItem) => <div>{formatBytes(item.bytes_scanned)}</div>,
    },
    {
      id: 7,
      label: "Warehouses",
      uid: "warehouses",
      render: (item: GetSnowflakeJobItem) => (
        <div className={cn("al-break-words", "al-line-clamp-2")}>
          {item.warehouse_names.join(", ")}
        </div>
      ),
      filter: {
        filterType: FilterType.DROPDOWN,
        searchKey: "warehouses",
        label: "Warehouses",
        options:
          filters?.warehouses.map(t => ({
            label: t,
            value: t,
          })) ?? [],
      },
    },
    {
      id: 8,
      label: "Integration",
      uid: "integration_name",
      render: (item: GetSnowflakeJobItem) => <span>{item.integration_name ?? "-"}</span>,
      filter: {
        filterType: FilterType.DROPDOWN,
        searchKey: "integration_names",
        label: "Integration",
        options:
          filters?.integration_names?.map(name => ({
            label: name,
            value: name,
          })) ?? [],
      },
    },
    {
      id: 9,
      label: "Integration Environment",
      uid: "integration_environment",
      render: (item: GetSnowflakeJobItem) => <span>{item.integration_environment ?? "-"}</span>,
      filter: {
        filterType: FilterType.DROPDOWN,
        searchKey: "integration_environments",
        label: "Integration Environment",
        getOptions: activeFilters => {
          const selectedIntegrations =
            (activeFilters.find(f => f.field === "integration_names")?.value as
              | string[]
              | undefined) ?? [];
          const seen = new Set<string>();
          const result: { label: string; value: string }[] = [];
          (filters?.integrations_with_environments ?? [])
            .filter(i => !selectedIntegrations.length || selectedIntegrations.includes(i.name))
            .flatMap(i => i.environments)
            .forEach(env => {
              if (!seen.has(env)) {
                seen.add(env);
                result.push({ label: env, value: env });
              }
            });
          return result;
        },
      },
    },
  ];
};
